import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, PackageX, Warehouse } from "lucide-react";

export default function SearchResults() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { isOn } = useFeatures();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const oc = params.get("oc"), dc = params.get("dc"), mode = params.get("mode") || "all";
  const ocity = params.get("ocity"), dcity = params.get("dcity");
  const [hubs, setHubs] = useState([]);
  const [hubSel, setHubSel] = useState("");
  const [hubMode, setHubMode] = useState(mode === "air" || mode === "sea" ? mode : "sea");
  const [hubDestCity, setHubDestCity] = useState(dcity || "");

  useEffect(() => {
    setLoading(true);
    api.get(`/hubs?country=${encodeURIComponent(oc || "")}`)
      .then(({ data }) => {
        const list = data.filter((h) => h.marketplace_active);
        setHubs(list);
        if (list.length > 0) setHubSel(list[0].id);
      })
      .catch(() => setHubs([]))
      .finally(() => setLoading(false));
  }, [oc, dc, mode, ocity, dcity]);

  const bookHub = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Connectez-vous en tant que client pour réserver."); return; }
    if (!hubDestCity.trim()) { toast.error("Indiquez la ville de destination"); return; }
    try {
      const { data } = await api.post("/shipments/hub", {
        hub_id: hubSel, dest_country: dc, dest_city: hubDestCity.trim(), mode: hubMode,
      });
      toast.success(`Expédition ${data.reference} créée — envoyez vos colis au Hub Colis2Go`);
      navigate(`/shipments/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la réservation");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-1">
          <Link to="/" className="hover:text-accent">{t("common.back")}</Link>
        </div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tight">{t("results.title")}</h1>
        <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-primary">
          <span>{ocity ? `${ocity}, ` : ""}{oc}</span>
          <ArrowRight className="h-5 w-5 text-accent" />
          <span>{dcity ? `${dcity}, ` : ""}{dc}</span>
        </div>

        {loading && <div className="mt-10 text-muted-foreground">{t("common.loading")}</div>}

        {hubs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="hub-option-card" className="mt-8 border-2 border-accent bg-accent/5 p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Warehouse className="h-5 w-5 text-accent" />
              <span className="font-heading font-bold text-xl">Hub Colis2Go</span>
              <Badge className="bg-accent text-white rounded-sm">Recommandé</Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              {isOn("operator_mode")
                ? "Envoyez vos marchandises à notre Hub Colis2Go. Nous les réceptionnons, vous décidez (attendre, consolider ou expédier tel quel), puis Colis2Go organise l'expédition vers votre destination. Consolidation jamais obligatoire."
                : "Envoyez vos marchandises à notre Hub Colis2Go. Nous les réceptionnons, vous décidez (attendre, consolider ou expédier tel quel), puis vous choisissez votre groupageur qui vient collecter au Hub. Consolidation jamais obligatoire."}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-4">
              <Select value={hubSel} onValueChange={setHubSel}>
                <SelectTrigger data-testid="hub-select" className="rounded-sm"><SelectValue placeholder="Choisir un Hub" /></SelectTrigger>
                <SelectContent>{hubs.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={hubMode} onValueChange={setHubMode}>
                <SelectTrigger data-testid="hub-mode-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="air">{t("search.air")}</SelectItem><SelectItem value="sea">{t("search.sea")}</SelectItem></SelectContent>
              </Select>
              <Input data-testid="hub-dest-city" className="rounded-sm" placeholder={`Ville de destination (${dc})`}
                value={hubDestCity} onChange={(e) => setHubDestCity(e.target.value)} />
              <Button data-testid="book-hub-btn" className="rounded-sm bg-accent hover:bg-accent/90 text-white" onClick={bookHub}>
                Envoyer au Hub
              </Button>
            </div>
          </motion.div>
        )}

        {!loading && hubs.length === 0 && (
          <div data-testid="no-results" className="mt-10 border border-dashed border-border p-12 text-center">
            <PackageX className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground max-w-md mx-auto">{t("results.none")}</p>
          </div>
        )}

      </div>
    </div>
  );
}
